import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { LLPStatusWarning } from '@avisoma/shared';
import { WarningDocumentLink as DocumentLink } from './WarningDocumentLink';

const formatCsn = (value: number) => value.toLocaleString('en-US');

function UnsignedWinnerLine({
  warning,
}: {
  warning: Extract<LLPStatusWarning, { code: 'unsigned-winner' }>;
}) {
  const alternative = warning.signedAlternative;
  return (
    <>
      Unsigned sheet published <DocumentLink documentId={warning.documentId} />.
      {alternative && (
        <>
          {' '}
          Signed alternative: <DocumentLink documentId={alternative.documentId} />
          {alternative.headerCsn != null && ` (header CSN ${formatCsn(alternative.headerCsn)})`}.
        </>
      )}
    </>
  );
}

function ClockRaisedLine({
  warning,
}: {
  warning: Extract<LLPStatusWarning, { code: 'clock-raised-by-single-sheet' }>;
}) {
  return (
    <>
      Header CSN {formatCsn(warning.headerCsn)} is above the voted engine CSN {formatCsn(warning.voteCsn)}.
      {' '}
      <DocumentLink documentId={warning.documentId} />
    </>
  );
}

function WarningText({ warning }: { warning: LLPStatusWarning }) {
  switch (warning.code) {
    case 'unsigned-winner':
      return <UnsignedWinnerLine warning={warning} />;
    case 'clock-raised-by-single-sheet':
      return <ClockRaisedLine warning={warning} />;
  }
}

/**
 * DR-4495: review flags on the elected LLP status sheet. Absent when the
 * winner is signed and its header sits inside the voted engine CSN band.
 */
export function LLPStatusWarnings({ warnings }: { warnings?: LLPStatusWarning[] }) {
  if (!warnings?.length) return null;

  return (
    <ul className="space-y-2 border-t border-gray-200 pt-4 dark:border-zinc-700">
      {warnings.map((warning, index) => (
        <li
          key={`${warning.code}-${warning.documentId}-${index}`}
          className="flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400"
        >
          <ExclamationTriangleIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            <WarningText warning={warning} />
          </span>
        </li>
      ))}
    </ul>
  );
}
